import React, { useRef } from 'react';
import { flushSync } from 'react-dom';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { BackIcon, CloseIcon, GlobeIcon, LocationIcon, SearchIcon, UsersIcon } from '../Brand/Icon';
import message from '../Brand/brandMessage';
import { getUser } from '../../utils/func';
import '../PostFilter/index.scss';
import './index.scss';

const filters = [
  { area: 'all', label: 'post.filter_all', Icon: GlobeIcon },
  { area: 'country', label: 'post.filter_local', Icon: LocationIcon },
  { area: 'friends', label: 'post.filter_friends', Icon: UsersIcon },
];

export default function StoryDetailFilters({
  mode, area = 'all', onAreaChange, onBack,
  searchOpen = false, onSearchOpen, onSearchClose,
  query = '', onQueryChange, onCompositionChange,
}) {
  const { t } = useTranslation();
  const input = useRef(null);
  const searchButton = useRef(null);
  const composing = useRef(false);
  const searching = mode === 'opinions' && searchOpen;

  const selectArea = (nextArea) => {
    if (nextArea !== 'all' && !getUser()) {
      message.warning(t(nextArea === 'country' ? 'brand:storyLocalSignIn' : 'brand:storyFriendsSignIn'));
      return;
    }
    if (nextArea !== area) onAreaChange?.(nextArea);
  };

  const openSearch = () => {
    // The input must exist and receive focus during the original user gesture
    // for mobile browsers to open the software keyboard on this first tap.
    flushSync(() => onSearchOpen?.());
    input.current?.focus({ preventScroll: true });
  };

  const closeSearch = event => {
    // Restore keyboard focus for Escape/keyboard activation. A pointer tap
    // should not inherit the text input's always-visible focus indicator.
    const restoreKeyboardFocus = !event || event.detail === 0;
    composing.current = false;
    flushSync(() => {
      onCompositionChange?.(false);
      onSearchClose?.();
    });
    if (restoreKeyboardFocus) searchButton.current?.focus({ preventScroll: true });
    else searchButton.current?.blur();
  };

  return <div className="wiwHeader post-filter story-detail-filters" data-mode={mode} data-area={area}>
    <div className="post-filter-row">
      {mode === 'statistics' ? <Button aria-label={t('brand:back')} onClick={onBack} icon={<BackIcon />} />
        : searching ? <Button aria-label={t('brand:back')} onClick={closeSearch} icon={<BackIcon />} />
          : <Button ref={searchButton} aria-label={t('brand:storySearchParticipants')} onClick={openSearch} icon={<SearchIcon />} />}
      {searching ? <div className="story-detail-search-field">
        <input ref={input} type="search" value={query} aria-label={t('brand:storySearchParticipants')}
          placeholder={t('brand:storySearchParticipants')} autoComplete="off" spellCheck={false} enterKeyHint="search" maxLength={120}
          onChange={event => onQueryChange?.(event.target.value)}
          onCompositionStart={() => { composing.current = true; onCompositionChange?.(true); }}
          onCompositionEnd={event => {
            composing.current = false;
            onQueryChange?.(event.currentTarget.value);
            onCompositionChange?.(false);
          }}
          onKeyDown={event => {
            if (composing.current || event.nativeEvent.isComposing || event.keyCode === 229) return;
            if (event.key === 'Escape') { event.preventDefault(); closeSearch(); }
            if (event.key === 'Enter') { event.preventDefault(); input.current?.blur(); }
          }} />
        <button type="button" className="story-detail-search-clear" aria-label={t('brand:searchClear')}
          tabIndex={query ? 0 : -1} style={{ visibility: query ? 'visible' : 'hidden' }}
          onClick={() => { onQueryChange?.(''); input.current?.focus({ preventScroll: true }); }}><CloseIcon /></button>
      </div> : filters.map(({ area: nextArea, label, Icon }) => <Button key={nextArea}
        aria-label={t(label)} aria-pressed={area === nextArea} onClick={() => selectArea(nextArea)}
        className={area === nextArea ? 'active-btn' : ''} icon={<Icon />} />)}
    </div>
  </div>;
}
